import { computed, ref, type Ref } from 'vue';
import { getCoData } from '@/infrastructure/api-functions';
import { E_ANALYSIS_CHART, E_CHART_TYPE, E_CO_OPERATE_TAB_TYPE, E_CHART_FIELD } from '@/types';
import { downloadImage } from '@eureka/utils/download-image';
import { useGlobalState } from './use-global-store';
import { generateImgTitle } from '@/infrastructure/utils/generate-img-title';
import { useI18n } from 'vue-i18n';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useCompanyBasic } from './use-company-basic';
import { hookErrorCode } from '@eureka/services/src/custom-error-handler'
import _ from 'lodash';
import { LIMITED_PERMISSION_CODE } from '@/constant';

export function useCoGraph(config: {
  graphChart: Ref<{
    generateChartImage: () => Promise<string>;
    drawChart: () => void;
    isChartLoaded: () => boolean;
    clearSelect: () => void;
    isEmptyChart: boolean
  }>;
}) {
    const { t } = useI18n();

    const { companyId, checkedChild, updateLimitInfo } = useGlobalState();

    const { companyInfo } = useCompanyBasic();

    const tabs = ref([
        {
            name: E_CO_OPERATE_TAB_TYPE.ASSIGNEE,
            label: t('coAssignee'),
        },
        {
            name: E_CO_OPERATE_TAB_TYPE.RESEARCH,
            label: t('coResearch'),
        }
    ]);
    const activeTab = ref(tabs.value[0].name);

    const title = computed(() => {
        const tab = tabs.value.find((tab) => tab.name === activeTab.value);
        return tab?.label;
    })

    function changeTab(tab: E_CO_OPERATE_TAB_TYPE) {
        activeTab.value = tab;
    }

    const chartComponent = computed(() => {
        return config.graphChart.value;
    });

    const isAssignee = computed(
        () => activeTab.value === E_CO_OPERATE_TAB_TYPE.ASSIGNEE
    );
    const chartKey = computed(() => isAssignee.value
        ? E_ANALYSIS_CHART.CO_ASSIGNEE_GRAPH
        : E_ANALYSIS_CHART.CO_RESEARCH_GRAPH)
    const chartType = E_CHART_TYPE.GRAPH;

    const isFetchLoading = ref(false);

    const isDownloadingImage = ref(false);

    const disableUI = computed(() => {
        return isFetchLoading.value || chartComponent.value?.isEmptyChart;
    });

    function chartConfig() {
        return import('@/constant/chart/graph.json').then((d) => {
            return d.default;
        });
    }

    function getGraphChartData() {
        isFetchLoading.value = true;

        const getCoDataPromise = getCoData({
            entity_id: companyId.value,
            include_children: checkedChild.value,
            chart_type: chartKey.value,
        })
            .finally(() => {
                isFetchLoading.value = false;
            });
        return hookErrorCode(getCoDataPromise, LIMITED_PERMISSION_CODE, () => {
            updateLimitInfo(true)
        });
    }

    function handleDrillDown(data: Record<string, string>) {
        const currentType = _.get(data, 'item._cfg.type');

        const currentSelectedId = currentType === 'node' ? _.get(data, 'item._cfg.id') : _.get(data, 'item._cfg.model.target');

        if (currentSelectedId === companyInfo.value?.ans_id) return;

        const field = isAssignee.value ? E_CHART_FIELD.ANCS_ID : E_CHART_FIELD.MASTER_AFFILIATION_ID;

        setTimeout(() => {
            openRightAside({
                title: isAssignee.value ? t('patent') : t('literature'),
                data: {
                    company_id: companyId.value,
                    chart_type: chartKey.value,
                    include_children: checkedChild.value,
                    dimension_list: [{ field, value: currentSelectedId }],
                },
                disabledBatchSave: !isAssignee.value,
                onClose: () => {
                    chartComponent.value?.clearSelect?.();
                },
            })
        }, 0);
    }

    async function downloadChartAsImage() {
        if (isDownloadingImage.value) return;
        isDownloadingImage.value = true;
        try {
            const imageStr = await chartComponent.value?.generateChartImage();
            downloadImage(
                imageStr,
                generateImgTitle(title.value)
            );
        } finally {
            isDownloadingImage.value = false;
        }
    }

    return {
        tabs,
        activeTab,
        changeTab,
        disableUI,
        isFetchLoading,
        chartType,
        chartKey,
        chartConfig,
        getGraphChartData,
        downloadChartAsImage,
        handleDrillDown,
    };
}
